import { useTheme } from '@mui/material';

import { useGetCohortAttritionByCohortProfileIdQuery } from '@verantos-dev/data-access-cohort-attrition';
import { useCohortBuilderData } from '@verantos-dev/data-access-cohorts';
import { DataGridMessages } from '@verantos-dev/shared-components';

import { CohortAttritionTableProps } from '../CohortAttritionTable';
import { useCohortAttritionColumns } from './useCohortAttritionColumns';
import { useCohortAttritionDataFormatter } from './useCohortAttritionDataFormatter';
import { useCohortAttritionHeaderMessageKeys } from './useCohortAttritionHeaderMessageKeys';
import { useCohortAttritionTableMessageKeys } from './useCohortAttritionTableMessageKeys';
import { useCohortAttritionWorkflowMessageKeys } from './useCohortAttritionWorkflowMessageKeys';

export const useCohortAttritionTable = ({
  projectId,
  cohortId,
}: Omit<CohortAttritionTableProps, 'cohortStatus'>) => {
  const theme = useTheme();
  const { data: cohortData } = useCohortBuilderData();
  const latestCohortStatus = cohortData?.data?.latestStatus;

  const attritionHeaderMessages = useCohortAttritionHeaderMessageKeys();
  const attritionTableMessageKeys = useCohortAttritionTableMessageKeys();
  const attritionWorkflowMessagesKey = useCohortAttritionWorkflowMessageKeys();

  const attritionTableMessages: DataGridMessages = {
    error: attritionTableMessageKeys.errorCohortEmptyMessage,
    errorTitle: attritionTableMessageKeys.emptyMessageTitle,
    noResults: attritionTableMessageKeys.defaultEmptyMessage,
    noRows: attritionTableMessageKeys.defaultEmptyMessage,
  };

  const attritionWorkflowMessages = {
    draftTitle: attritionWorkflowMessagesKey.draftTitle,
    draftMessage: attritionWorkflowMessagesKey.draftMessage,
    errorTitle: attritionWorkflowMessagesKey.errorTitle,
    errorMessage: attritionWorkflowMessagesKey.errorMessage,
    processingTitle: attritionWorkflowMessagesKey.processingTitle,
    processingMessage: attritionWorkflowMessagesKey.processingMessage,
  };

  const columns = useCohortAttritionColumns();

  const useFetchCohortAttritionData = () => {
    const { data: unformattedData, ...rest } = useGetCohortAttritionByCohortProfileIdQuery({
      cohortProfileId: cohortId,
      projectId,
    });
    const formattedData = useCohortAttritionDataFormatter(unformattedData);

    return {
      data: {
        ...unformattedData,
        data: formattedData,
      },
      ...rest,
    };
  };
  const {
    isUninitialized,
    isFetching,
    data: attritionData,
    error: attritionDataError,
  } = useFetchCohortAttritionData();

  return {
    theme,
    latestCohortStatus,
    isInitiallyLoading: isUninitialized || isFetching,
    columns,
    attritionHeaderMessages,
    attritionTableMessages,
    attritionWorkflowMessages,
    attritionData,
    attritionDataError,
    useFetchCohortAttritionData,
  };
};
